import Spinner from "components/Spinner";
import { setNotification } from "features/Notification/notificationSlice";
import { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { useDispatch } from "react-redux";
import authHeader from "services/auth-header";
import API from "services/axios";
import useInbox from "../InboxTab/InboxContext";

const STATUS_OPTIONS = ["pending", "completed"];
const normalizeStatus = (value) => {
  const lowerValue = (value || "").toLowerCase();
  return STATUS_OPTIONS.includes(lowerValue) ? lowerValue : STATUS_OPTIONS[0];
};

let formFields = [
  {
    name: "Project Name",
    type: "text",
    placeholder: "Enter project name",
    value: "name",
  },
  {
    name: "Art Id",
    type: "text",
    placeholder: "Enter art id",
    value: "artworkId",
  },
];

export default function EAddProject({
  show,
  hideModal,
  isUpdateMode = false,
  initialData = null,
  onSaved,
}) {
  const dispatch = useDispatch();

  /* ******************** All States ************************* */
  const [formData, setFormData] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [newTeamEmail, setNewTeamEmail] = useState("");
  const [editIndex, setEditIndex] = useState(null);
  const [editEmail, setEditEmail] = useState("");

  const [loading, setLoading] = useState(false);

  const { selectedGrantEmail } = useInbox();

  /* ******************** All Functions ************************* */

  useEffect(() => {
    if (!show) return;

    if (initialData) {
      const base = initialData || {};
      setFormData({
        ...base,
        name: base.name || "",
        artworkId: base.artworkId || "",
        status: normalizeStatus(base.status),
      });
      setTeamMembers(base.teamMembers || []);
    } else {
      setFormData({ status: STATUS_OPTIONS[0] });
      setTeamMembers([]);
    }

    setNewTeamEmail("");
    setEditIndex(null);
    setEditEmail("");
  }, [show, initialData, isUpdateMode]);

  const handleAddTeamMember = () => {
    const email = newTeamEmail.trim();
    if (!email) return;

    setTeamMembers((prev) => [...prev, email]);
    setNewTeamEmail("");
  };

  const handleDeleteTeamMember = (index) => {
    setTeamMembers((prev) => prev.filter((_, i) => i !== index));
    if (editIndex === index) {
      setEditIndex(null);
      setEditEmail("");
    }
  };

  const handleEditClick = (index) => {
    setEditIndex(index);
    setEditEmail(teamMembers[index] || "");
  };

  const handleUpdateTeamMember = () => {
    const email = editEmail.trim();
    if (!email || editIndex === null) return;

    setTeamMembers((prev) =>
      prev.map((item, idx) => (idx === editIndex ? email : item)),
    );
    setEditIndex(null);
    setEditEmail("");
  };

  const handleCancelEdit = () => {
    setEditIndex(null);
    setEditEmail("");
  };

  //  Add / Update Project
  const handleSaveProject = async () => {
    try {
      setLoading(true);
      const normalizedStatus = normalizeStatus(formData?.status);
      const payload = {
        ...(formData || {}),
        id: initialData?.id ?? formData?.id,
        teamMembers,
        grantEmail: selectedGrantEmail,
        status: normalizedStatus,
      };

      let response;
      if (isUpdateMode) {
        response = await API.patch("/email-artwork", payload, {
          headers: authHeader(),
        });
      } else {
        response = await API.post("/email-artwork", payload, {
          headers: authHeader(),
        });
      }

      const { data } = response;
      if (data?.success) {
        dispatch(
          setNotification({
            message: isUpdateMode
              ? "Project updated successfully"
              : "Project added successfully",
            type: "success",
          }),
        );
        if (typeof onSaved === "function") {
          onSaved(data);
        }
        hideModal();
        setFormData(null);
        setTeamMembers([]);
        setNewTeamEmail("");
        setEditIndex(null);
        setEditEmail("");
      } else {
        throw new Error(data?.message || "Failed to save project");
      }
    } catch (err) {
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  /* ******************** All UseEffects ************************* */

  return (
    <div>
      {loading && <Spinner />}
      {/* Modal Header */}
      <Modal.Header className="py-0" closeButton onHide={hideModal}>
        <Modal.Title>
          {isUpdateMode ? "Update Project" : "Add New Project"}
        </Modal.Title>
      </Modal.Header>

      {/* Modal Body */}
      <Modal.Body>
        {/* Form fields */}
        {formFields.map((field, index) => (
          <div key={index} className="mb-2">
            <label className="form-label mb-0">{field.name}</label>
            <input
              type={field.type}
              className="form-control"
              placeholder={field.placeholder}
              value={formData ? formData[field.value] || "" : ""}
              onChange={(e) =>
                setFormData({
                  ...(formData || {}),
                  [field.value]: e.target.value,
                })
              }
            />
          </div>
        ))}
        <div className="mb-2">
          <label className="form-label mb-0">Status</label>
          <select
            className="form-select"
            value={(formData && formData.status) || STATUS_OPTIONS[0]}
            onChange={(e) =>
              setFormData({
                ...(formData || {}),
                status: e.target.value,
              })
            }
          >
            {STATUS_OPTIONS.map((status) => (
              <option key={status} value={status}>
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </option>
            ))}
          </select>
        </div>
        {/* ******************** Add Team Members ************************* */}
        <div>
          {/* Add Team Crud */}

          <div className="d-flex align-items-center justify-content-between mt-3">
            <h4 className="mb-0">Team Members</h4>
          </div>
          <hr className="mt-1 mb-2" />

          <form
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleAddTeamMember();
            }}
            className="d-flex align-items-center gap-2 mb-2"
          >
            <input
              type="email"
              className="form-control form-control-sm"
              placeholder="Enter team member email"
              value={newTeamEmail}
              onChange={(e) => setNewTeamEmail(e.target.value)}
            />
            <button
              type="button"
              className="btn btn-sm btn-outline-primary flex-center"
              onClick={handleAddTeamMember}
            >
              <i className="mdi mdi-plus fs-5"></i>
            </button>
          </form>

          {teamMembers && teamMembers.length > 0 && (
            <div className="table-responsive mt-2">
              <table className="table table-sm mb-0">
                <thead>
                  <tr>
                    <th style={{ width: "40px" }}>#</th>
                    <th>Email</th>
                    <th style={{ width: "120px" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {teamMembers.map((email, index) => (
                    <tr key={index}>
                      <td>{index + 1}</td>
                      <td>
                        {editIndex === index ? (
                          <input
                            type="email"
                            className="form-control form-control-sm"
                            value={editEmail}
                            onChange={(e) => setEditEmail(e.target.value)}
                          />
                        ) : (
                          email
                        )}
                      </td>
                      <td>
                        <div className="d-flex justify-content-end align-items-center">
                          {editIndex === index ? (
                            <>
                              <button
                                type="button"
                                className="btn btn-sm py-0 px-1 btn-success me-1"
                                onClick={handleUpdateTeamMember}
                              >
                                Save
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm py-0 px-1 btn-secondary"
                                onClick={handleCancelEdit}
                              >
                                Cancel
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                type="button"
                                className="btn btn-sm py-0 px-1 btn-outline-secondary me-1"
                                onClick={() => handleEditClick(index)}
                              >
                                <i className="mdi mdi-pencil"></i>
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm py-0 px-1 btn-outline-danger"
                                onClick={() => handleDeleteTeamMember(index)}
                              >
                                <i className="mdi mdi-delete"></i>
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Modal.Body>

      {/* Modal Footer */}
      <Modal.Footer>
        <button
          type="button"
          className="btn btn-sm btn-secondary"
          onClick={hideModal}
        >
          Close
        </button>
        <button
          type="button"
          onClick={handleSaveProject}
          className="btn btn-sm btn-primary"
        >
          {isUpdateMode ? "Update Project" : "Save Project"}
        </button>
      </Modal.Footer>
    </div>
  );
}
